import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { branding } from '../constants/branding';
import { getApiBaseUrl } from '../utils/apiConfig';
import { apiService } from '../services/apiService';
import { TRUELAYER_PENDING_PAYMENT_ID_KEY } from '../utils/truelayerPaymentAuth';

const REDIRECT_SECONDS = 10;

type CallbackApiBody = {
  status?: string;
  code?: number;
  message?: string;
  data?: { connected?: boolean; userId?: number };
  errors?: string[] | null;
};

let inFlightCallbackKey: string | null = null;
let inFlightCallbackPromise: Promise<{ ok: boolean; body: CallbackApiBody | null }> | null = null;

function runTrueLayerCallbackOnce(code: string, state: string, scope: string) {
  const key = `${state}:${code}`;
  if (inFlightCallbackKey === key && inFlightCallbackPromise) {
    return inFlightCallbackPromise;
  }
  inFlightCallbackKey = key;
  inFlightCallbackPromise = (async () => {
    try {
      const params = new URLSearchParams();
      params.set('code', code);
      params.set('state', state);
      if (scope) {
        params.set('scope', scope);
      }
      const response = await fetch(`${getApiBaseUrl()}/api/banking/truelayer/callback?${params.toString()}`, {
        method: 'GET',
        credentials: 'include',
      });
      let body: CallbackApiBody | null = null;
      try {
        body = (await response.json()) as CallbackApiBody;
      } catch {
        body = null;
      }
      return { ok: response.ok, body };
    } finally {
      inFlightCallbackKey = null;
      inFlightCallbackPromise = null;
    }
  })();
  return inFlightCallbackPromise;
}

function isSuccessPayload(body: CallbackApiBody | null): boolean {
  if (!body) return false;
  const st = (body.status || '').toLowerCase();
  if (st === 'success' && body.code === 200 && body.data?.connected === true) {
    return true;
  }
  if (st === 'success' && body.data?.connected === true) {
    return true;
  }
  return false;
}

function isLikelyAlreadyUsedCodeError(body: CallbackApiBody | null, httpOk: boolean): boolean {
  if (httpOk || !body) return false;
  const msg = `${body.message || ''} ${(body.errors || []).join(' ')}`.toLowerCase();
  return (
    msg.includes('invalid_grant') ||
    msg.includes('already been used') ||
    msg.includes('code has already') ||
    (msg.includes('authorization code') && msg.includes('invalid'))
  );
}

/** OAuth params may appear in query or hash (some flows use fragment). */
function readOAuthParamsFromWindow(): { code: string; state: string; scope: string; error: string; errorDescription: string } {
  if (typeof window === 'undefined') {
    return { code: '', state: '', scope: '', error: '', errorDescription: '' };
  }
  const search = new URLSearchParams(window.location.search);
  const hash =
    window.location.hash && window.location.hash.length > 1
      ? new URLSearchParams(window.location.hash.replace(/^#/, ''))
      : new URLSearchParams();
  const pick = (k: string) => search.get(k) || hash.get(k) || '';
  return {
    code: pick('code'),
    state: pick('state'),
    scope: pick('scope'),
    error: pick('error'),
    errorDescription: pick('error_description'),
  };
}

function readPaymentStatusFromApiData(data: unknown): string {
  if (!data || typeof data !== 'object') return 'unknown';
  const d = data as Record<string, unknown>;
  const nested = d.payment as Record<string, unknown> | undefined;
  const s =
    (typeof d.status === 'string' && d.status) ||
    (nested && typeof nested.status === 'string' && nested.status) ||
    '';
  return s || 'unknown';
}

const TrueLayerConnect: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Finalizing bank connection...');
  const [secondsLeft, setSecondsLeft] = useState(REDIRECT_SECONDS);
  const [postPaymentRedirect, setPostPaymentRedirect] = useState<'dashboard' | 'payments'>('dashboard');

  const fromRouter = useMemo(
    () => ({
      code: searchParams.get('code') || '',
      state: searchParams.get('state') || '',
      scope: searchParams.get('scope') || '',
      error: searchParams.get('error') || '',
      errorDescription: searchParams.get('error_description') || '',
    }),
    [searchParams]
  );

  useEffect(() => {
    let mounted = true;

    const completeCallback = async () => {
      const merged = readOAuthParamsFromWindow();
      const code = merged.code || fromRouter.code;
      const state = merged.state || fromRouter.state;
      const scope = merged.scope || fromRouter.scope;
      const error = merged.error || fromRouter.error;
      const errorDescription = merged.errorDescription || fromRouter.errorDescription;

      if (error) {
        if (!mounted) return;
        setStatus('error');
        setMessage(errorDescription || error || 'Bank consent was not completed.');
        return;
      }

      if (code && state) {
        try {
          localStorage.removeItem(TRUELAYER_PENDING_PAYMENT_ID_KEY);
        } catch {
          /* ignore */
        }

        try {
          const { ok, body } = await runTrueLayerCallbackOnce(code, state, scope);

          if (!mounted) return;

          if (isSuccessPayload(body)) {
            setStatus('success');
            setMessage(body?.message || 'Bank account connected successfully. Redirecting to dashboard...');
            return;
          }

          if (isLikelyAlreadyUsedCodeError(body, ok)) {
            setStatus('success');
            setMessage(
              'Bank connection was already completed (this page was opened more than once). You can continue — redirecting to dashboard...'
            );
            return;
          }

          if (ok && body) {
            setStatus('success');
            setMessage(body.message || 'Bank account connected successfully. Redirecting to dashboard...');
            return;
          }

          const detail =
            body?.errors?.[0] ||
            body?.message ||
            (!ok ? 'We could not confirm the connection from this page.' : 'Unexpected response from server.');
          setStatus('error');
          setMessage(
            `${detail} If your dashboard shows linked accounts after refresh, you can ignore this message.`
          );
        } catch {
          if (!mounted) return;
          setStatus('error');
          setMessage(
            'We could not reach the server from this page. Your bank may still have connected — open the dashboard and use “I’ve completed consent” to refresh.'
          );
        }
        return;
      }

      let pendingPaymentId: string | null = null;
      try {
        pendingPaymentId = localStorage.getItem(TRUELAYER_PENDING_PAYMENT_ID_KEY);
      } catch {
        pendingPaymentId = null;
      }

      if (pendingPaymentId) {
        if (!mounted) return;
        setMessage('Checking payment status after bank authorisation...');

        try {
          const res = await apiService.get<Record<string, unknown>>(
            `/api/banking/truelayer/payments/${encodeURIComponent(pendingPaymentId)}`
          );
          if (!mounted) return;

          try {
            localStorage.removeItem(TRUELAYER_PENDING_PAYMENT_ID_KEY);
          } catch {
            /* ignore */
          }

          if (res.success && res.data) {
            const ps = readPaymentStatusFromApiData(res.data);
            setPostPaymentRedirect('payments');
            setStatus('success');
            setMessage(
              `Bank authorisation finished. Payment status: ${ps}. You can review the payment on the Payments page.`
            );
            return;
          }

          setPostPaymentRedirect('payments');
          setStatus('success');
          setMessage(
            'You returned from the bank after a payment. If status does not update, open Payments and refresh.'
          );
        } catch {
          if (!mounted) return;
          setPostPaymentRedirect('payments');
          setStatus('success');
          setMessage(
            'You returned from the bank after a payment. Open Payments to confirm the latest status.'
          );
        }
        return;
      }

      if (!mounted) return;
      setStatus('error');
      setMessage(
        'No bank linking details were found in this URL. If you just authorised a payment, this page may have opened without parameters — try Payments. If you were linking your bank account, start again from the dashboard.'
      );
    };

    completeCallback();
    return () => {
      mounted = false;
    };
  }, [fromRouter.code, fromRouter.error, fromRouter.errorDescription, fromRouter.scope, fromRouter.state]);

  useEffect(() => {
    if (status === 'processing') return;
    if (secondsLeft <= 0) {
      navigate(postPaymentRedirect === 'payments' ? '/payments' : '/dashboard', { replace: true });
      return;
    }
    const timer = window.setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [status, secondsLeft, navigate, postPaymentRedirect]);

  const statusColor = status === 'success' ? '#0dca9d' : status === 'error' ? '#ff7171' : '#086afb';
  const title =
    status === 'success'
      ? postPaymentRedirect === 'payments'
        ? 'Payment authorisation'
        : 'Bank Connection Complete'
      : status === 'error'
        ? 'Could Not Confirm Here'
        : 'Completing Connection';

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: branding.pageBackground,
        padding: 16,
        fontFamily: branding.fontFamily,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 520,
          background: '#fff',
          borderRadius: 16,
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
          padding: 24,
          border: `1px solid ${statusColor}22`,
        }}
      >
        <h1 style={{ margin: 0, fontSize: 24, color: '#1f2937' }}>{title}</h1>
        <p style={{ marginTop: 12, marginBottom: 0, color: '#374151', fontSize: 15 }}>{message}</p>

        {status !== 'processing' && (
          <p style={{ marginTop: 16, marginBottom: 0, color: '#6b7280', fontSize: 13 }}>
            Redirecting to {postPaymentRedirect === 'payments' ? 'Payments' : 'dashboard'} in {secondsLeft}s...
          </p>
        )}
        {status === 'error' && (
          <button
            type="button"
            onClick={() => navigate('/dashboard', { replace: true })}
            style={{
              marginTop: 16,
              padding: '10px 16px',
              borderRadius: 10,
              border: 'none',
              background: '#086afb',
              color: '#fff',
              fontWeight: 600,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            Go to dashboard now
          </button>
        )}
        {status === 'success' && postPaymentRedirect === 'payments' && (
          <button
            type="button"
            onClick={() => navigate('/payments', { replace: true })}
            style={{
              marginTop: 16,
              marginLeft: 8,
              padding: '10px 16px',
              borderRadius: 10,
              border: '1px solid #086afb',
              background: '#fff',
              color: '#086afb',
              fontWeight: 600,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            Open Payments
          </button>
        )}
      </div>
    </div>
  );
};

export default TrueLayerConnect;
